// src/components/staff/ReturnAssetModal.js
import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const API_URL = "https://ims.holdthechild.net";

export default function ReturnAssetModal({ asset, closeModal }) {
    // Initialize reason to an empty string, or a default value if preferred
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Basic validation for reason selection
        if (!reason || reason === "") {
            toast.error("Please select a reason for the return.");
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error("Authentication token not found.");
            }

            await axios.post(
                `${API_URL}/api/return-requests`,
                {
                    assetId: asset._id,
                    reason: reason, // Use the selected reason from the dropdown
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            toast.success("Asset return request submitted successfully! 🎉");
            closeModal(); // Close modal and trigger parent to refresh assets
        } catch (error) {
            console.error("Error submitting return request:", error);
            const errorMessage =
                error.response?.data?.message ||
                "Failed to submit return request. Please try again.";
            toast.error(`Error: ${errorMessage} 😥`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-auto">
                <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
                    <h2 className="text-2xl font-bold text-orange-600">Request Asset Return</h2>
                    <button
                        type="button"
                        onClick={closeModal}
                        className="text-3xl text-gray-400 hover:text-red-500 font-light"
                    >
                        &times;
                    </button>
                </div>

                {/* Asset Details Section */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Asset Details</h3>
                    <div className="grid grid-cols-1 gap-2 text-sm">
                        <p><span className="font-medium text-gray-700">Name:</span> {asset?.name || 'N/A'}</p>
                        <p><span className="font-medium text-gray-700">Serial No:</span> {asset?.serialNumber || 'N/A'}</p>
                        <p><span className="font-medium text-gray-700">Category:</span> {asset?.category || 'N/A'}</p>
                        <p><span className="font-medium text-gray-700">Current Status:</span> {asset?.status || 'N/A'}</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="mb-6">
                        <label htmlFor="reason" className="block text-gray-700 text-sm font-semibold mb-2">
                            Reason for Return <span className="text-red-500">*</span>
                        </label>
                        <select
                            id="reason"
                            name="reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-800"
                            required
                        >
                            <option value="">-- Select a Reason --</option>
                            <option value="Broken">Broken / Damaged</option>
                            <option value="No Longer Needed">No Longer Needed</option>
                            <option value="Upgrade">Upgrading to a new asset</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>

                    <p className="text-sm text-gray-600 mb-6 text-center">
                        Your request will be sent to the Operations Associate for review.
                    </p>

                    <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={closeModal}
                            disabled={loading}
                            className="px-5 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition duration-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-5 py-2 rounded-md bg-orange-600 text-white font-semibold hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:bg-orange-400 transition duration-200"
                        >
                            {loading ? "Submitting..." : "Submit Request"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
